import { useAuth } from '../contexts/AuthContext';
import type { V2Capabilities } from '../types/v2';

/**
 * Default capabilities applied when the backend response does not include
 * a `capabilities` object — for example, an older backend version. We default
 * to "everything allowed" because (a) the backend remains authoritative and
 * will 403 any forbidden action anyway, (b) we should not surprise users on
 * old clients by hiding controls based on missing data.
 */
const DEFAULTS_ALL_ENABLED: V2Capabilities = {
  can_withdraw: true,
  can_change_password: true,
  can_create_account: true,
  can_create_card_deposit: true,
  can_send_chat_message: true,
  can_mark_chat_read: true,
  can_mark_docs_viewed: true,
  can_mark_push_viewed: true,
  can_view_card_secrets: true,
  can_block_card: true,
  can_register_fcm: true,
};

/**
 * useCapabilities — read server-derived UX hints for the current session.
 *
 * Each flag tells the UI whether a given action will succeed for the
 * authenticated user. Under impersonation, destructive actions are forced
 * to `false` so the corresponding buttons can be hidden or disabled.
 *
 * Usage:
 *   const { canWithdraw } = useCapabilities();
 *   {canWithdraw && <WithdrawButton />}
 *
 * Important: this hook is for UX only. The authoritative policy lives in
 * the backend (middleware/impersonation_policy.go). Never trust these flags
 * for security decisions; the server will still enforce restrictions.
 */
export function useCapabilities(): V2Capabilities {
  const { user } = useAuth();
  return user?.capabilities ?? DEFAULTS_ALL_ENABLED;
}
